import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import { Camera, Brain, FileText, ArrowRight } from 'lucide-react';

const TechnologySummary = () => {
  const { language } = useLanguage();
  
  const t = (key) => getTranslation(language, key);
  const langPrefix = language === 'en' ? '' : `/${language}`;

  const technologies = [
    {
      icon: Camera,
      titleKey: 'home.tech.veoTitle',
      descKey: 'home.tech.veoDesc',
      fallbackTitle: 'Veo Camera System',
      fallbackDesc: 'Professional match recording with AI-powered analysis and automatic highlights'
    },
    {
      icon: Brain,
      titleKey: 'home.tech.draftTitle',
      descKey: 'home.tech.draftDesc',
      fallbackTitle: 'The Draft App',
      fallbackDesc: 'AI-powered assessment of physical and technical abilities with age-appropriate benchmarking'
    },
    {
      icon: FileText,
      titleKey: 'home.tech.reportsTitle',
      descKey: 'home.tech.reportsDesc',
      fallbackTitle: 'Evaluation Reports',
      fallbackDesc: 'Comprehensive multi-page analysis with personalized recommendations for continued development'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-500 rounded-full filter blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm font-semibold mb-4">
            {t('home.tech.badge') || 'Cutting-Edge Technology'}
          </span>
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            {t('home.tech.title') || 'Train Smarter with Technology'}
          </h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {t('home.tech.subtitle') || 'We use the latest technology to track, analyze, and accelerate your football development'}
          </p>
        </div>

        {/* Technology Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {technologies.map((tech, index) => (
            <Card 
              key={index} 
              className="p-8 bg-white/10 backdrop-blur-lg border-white/20 hover:bg-white/15 transition-all duration-300 group"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-green-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <tech.icon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl font-black mb-3 text-white">
                {t(tech.titleKey) || tech.fallbackTitle}
              </h3>
              <p className="text-blue-100 leading-relaxed">
                {t(tech.descKey) || tech.fallbackDesc}
              </p>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to={`${langPrefix}/technology`}>
            <Button 
              size="lg" 
              className="bg-white text-slate-900 hover:bg-blue-100 font-bold px-8 py-6 text-lg group"
            >
              {t('home.tech.cta') || 'Explore Our Technology'}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TechnologySummary;
